/// <amd-module name="Wit/BoardsHub/Common/ColumnsValidator" />
import { IObservableValue, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { containsControlChars, containsMismatchedSurrogateChars, format } from "VSS/Core/Util/String";
import { BoardColumn } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import * as Resources from "Wit/BoardsHub/Common/Resources";

type IAllowedMappings = { [columnType: string]: { [workItemType: string]: string[] } };

export interface IColumnsValidator {
    /**
     * An observable whose value is true if there are no errors.
     */
    readonly isValid: IReadonlyObservableValue<boolean>;
    /**
     * Re-evaluates whether errors are present
     */
    validate(columns: BoardColumn[]): void;
    /**
     * @returns an observable whose value is true if there are no errors for the
     * given column. This will be a constant object for each column.
     */
    isColumnValid(columnId: string): IReadonlyObservableValue<boolean>;
    /**
     * @returns an observable whose value is a string representing the error
     * present for the name of the given column if such an error exists. The
     * value is undefined if there is no errror. This will be a constant object
     * for each column.
     */
    getNameError(columnId: string): IReadonlyObservableValue<string | undefined>;
    /**
     * @returns an observable whose value is a string representing the error
     * present for the work in progress limit of the given column if such an
     * error exists. The value is undefined if there is no errror. This will be
     * a constant object for each column.
     */
    getWIPLimitError(columnId: string): IReadonlyObservableValue<string | undefined>;
    /**
     * @returns an observable whose value is a string representing the error
     * present for the definition of done for the given column if such an
     * error exists. The value is undefined if there is no errror. This will be
     * a constant object for each column.
     */
    getDefinitionOfDoneError(columnId: string): IReadonlyObservableValue<string | undefined>;
    /**
     * @returns an observable whose value is a string representing the error
     * present for the state mapping of the given column and WIT if such an
     * error exists. The value is undefined if there is no errror. This will be
     * a constant object for each column and WIT.
     */
    getStateMappingError(columnId: string, workItemTypeName: string): IReadonlyObservableValue<string | undefined>;
}

export class ColumnsValidator implements IColumnsValidator {
    private static readonly minWIPLimit = 0;
    private static readonly maxWIPLimit = 999;
    private static readonly maxDefinitionOfDoneLength = 2000;
    private static readonly maxColumnNameLength = 256;

    public isValid: IObservableValue<boolean> = new ObservableValue(true);

    private _isValidByColumn: Map<string, IObservableValue<boolean>> = new Map();
    private _nameErrorByColumn: Map<string, IObservableValue<string | undefined>> = new Map();
    private _wipLimitErrorByColumn: Map<string, IObservableValue<string | undefined>> = new Map();
    private _definitionOfDoneErrorByColumn: Map<string, IObservableValue<string | undefined>> = new Map();
    private _stateMappingErrorsByColumn: Map<string, Map<string, IObservableValue<string | undefined>>> = new Map();

    constructor(private _allowedMappings: IAllowedMappings) {}

    public validate(columns: BoardColumn[]): void {
        this.clear();
        const idsByName: Map<string, string[]> = new Map();

        for (const column of columns) {
            if (idsByName.has(column.name)) {
                idsByName.get(column.name)!.push(column.id);
            } else {
                idsByName.set(column.name, [column.id]);
            }

            const idsWithSameName = idsByName.get(column.name)!;
            if (idsWithSameName.length > 1) {
                for (const id of idsWithSameName) {
                    this.setNameError(id, Resources.ColumnNameDuplicateError);
                }
            }

            if (!column.name) {
                this.setNameError(column.id, Resources.ColumnNameMissingError);
            } else if (column.name.length >= ColumnsValidator.maxColumnNameLength) {
                this.setNameError(column.id, format(Resources.ColumnNameTooLongErrorFormat, ColumnsValidator.maxColumnNameLength));
            } else if (containsControlChars(column.name) || containsMismatchedSurrogateChars(column.name)) {
                this.setNameError(column.id, Resources.ColumnNameInvalidCharactersError);
            }

            if (
                (!column.itemLimit && column.itemLimit !== 0) ||
                column.itemLimit < ColumnsValidator.minWIPLimit ||
                column.itemLimit > ColumnsValidator.maxWIPLimit ||
                Math.floor(column.itemLimit) !== column.itemLimit
            ) {
                this.setWIPLimitError(
                    column.id,
                    format(Resources.WorkInProgressLimitInvalidErrorFormat, ColumnsValidator.minWIPLimit, ColumnsValidator.maxWIPLimit)
                );
            }

            const allowedMappingsForColumn = this._allowedMappings["" + column.columnType] ?? {};
            for (const [workItemTypeName, allowedStatesForWIT] of Object.entries(allowedMappingsForColumn)) {
                const state = column.stateMappings[workItemTypeName];
                if (!allowedStatesForWIT.includes(state)) {
                    this.setStateMappingError(column.id, workItemTypeName, format(Resources.StateMappingInvalidError, state, workItemTypeName));
                }
            }

            if (column.description && column.description.length > ColumnsValidator.maxDefinitionOfDoneLength) {
                this.setDefinitionOfDoneError(
                    column.id,
                    format(Resources.DefinitionOfDoneTooLongErrorFormat, column.description.length, ColumnsValidator.maxDefinitionOfDoneLength)
                );
            }
        }
    }

    private clear(): void {
        const errorMapsToClear = [
            this._nameErrorByColumn,
            this._wipLimitErrorByColumn,
            this._definitionOfDoneErrorByColumn,
            ...this._stateMappingErrorsByColumn.values()
        ];

        for (const map of errorMapsToClear) {
            for (const obs of map.values()) {
                obs.value = undefined;
            }
        }
        for (const obs of this._isValidByColumn.values()) {
            obs.value = true;
        }
        this.isValid.value = true;
    }

    public isColumnValid(columnId: string): IReadonlyObservableValue<boolean> {
        if (!this._isValidByColumn.has(columnId)) {
            this._isValidByColumn.set(columnId, new ObservableValue(true));
        }
        return this._isValidByColumn.get(columnId)!;
    }

    private setColumnInvalid(coliumnId: string): void {
        if (this._isValidByColumn.has(coliumnId)) {
            this._isValidByColumn.get(coliumnId)!.value = false;
        } else {
            this._isValidByColumn.set(coliumnId, new ObservableValue(false));
        }
        this.isValid.value = false;
    }

    public getNameError(columnId: string): IReadonlyObservableValue<string | undefined> {
        return this.getError(columnId, this._nameErrorByColumn);
    }

    private setNameError(columnId: string, error: string): void {
        this.setError(columnId, error, this._nameErrorByColumn);
    }

    public getWIPLimitError(columnId: string): IReadonlyObservableValue<string | undefined> {
        return this.getError(columnId, this._wipLimitErrorByColumn);
    }

    private setWIPLimitError(columnId: string, error: string): void {
        this.setError(columnId, error, this._wipLimitErrorByColumn);
    }

    public getDefinitionOfDoneError(columnId: string): IReadonlyObservableValue<string | undefined> {
        return this.getError(columnId, this._definitionOfDoneErrorByColumn);
    }

    private setDefinitionOfDoneError(columnId: string, error: string): void {
        this.setError(columnId, error, this._definitionOfDoneErrorByColumn);
    }

    public getStateMappingError(columnId: string, workItemTypeName: string): IReadonlyObservableValue<string | undefined> {
        if (!this._stateMappingErrorsByColumn.has(columnId)) {
            this._stateMappingErrorsByColumn.set(columnId, new Map());
        }
        const stateMappingErrorByWorkItemType = this._stateMappingErrorsByColumn.get(columnId)!;

        return this.getError(workItemTypeName, stateMappingErrorByWorkItemType);
    }

    private setStateMappingError(columnId: string, workItemTypeName: string, error: string): void {
        if (!this._stateMappingErrorsByColumn.has(columnId)) {
            this._stateMappingErrorsByColumn.set(columnId, new Map());
        }
        const stateMappingErrorByWorkItemType = this._stateMappingErrorsByColumn.get(columnId)!;
        this.setError(workItemTypeName, error, stateMappingErrorByWorkItemType);
    }

    private getError(key: string, map: Map<string, IObservableValue<string | undefined>>): IReadonlyObservableValue<string | undefined> {
        if (!map.has(key)) {
            map.set(key, new ObservableValue(undefined));
        }
        return map.get(key)!;
    }

    private setError(key: string, error: string, map: Map<string, IObservableValue<string | undefined>>): void {
        if (map.has(key)) {
            map.get(key)!.value = error;
        } else {
            map.set(key, new ObservableValue(error));
        }

        if (error) {
            this.setColumnInvalid(key);
        }
    }
}
